import { inject, Injectable, NgZone } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AutoLogOutService {
  private readonly ngZone = inject(NgZone);
  private readonly INACTIVITY_TIMEOUT = 1799969;
  private readonly SESSION_TIMEOUT = 14399969;
  private readonly CHECK_FREQUENCY = 30000;
  private checkInterval: ReturnType<typeof setInterval> | null = null;
  private logoutCallback: (() => void) | null = null;

  private readonly LAST_ACTIVITY_KEY = 'session_last_activity';
  private readonly LOGIN_TIME_KEY = 'session_login_time';

  startMonitoring(logoutFn: () => void): void {
    this.logoutCallback = logoutFn;
    if (!sessionStorage.getItem(this.LOGIN_TIME_KEY)) {
      const now = Date.now().toString();
      sessionStorage.setItem(this.LOGIN_TIME_KEY, now);
      sessionStorage.setItem(this.LAST_ACTIVITY_KEY, now);
    }
    this.startInterval();
    this.startEventListeners();
  }

  stopMonitoring(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.removeEventListerns();
    sessionStorage.removeItem(this.LOGIN_TIME_KEY);
    sessionStorage.removeItem(this.LAST_ACTIVITY_KEY);
  }

  private startInterval(): void {
    this.ngZone.runOutsideAngular(() => {
      this.checkInterval = setInterval(() => {
        this.checkStatus();
      }, this.CHECK_FREQUENCY);
    });
  }

  private checkStatus(): void {
    const now = Date.now();
    const loginTimeStr = sessionStorage.getItem(this.LOGIN_TIME_KEY);
    const lastActivityStr = sessionStorage.getItem(this.LAST_ACTIVITY_KEY);

    if (!loginTimeStr || !lastActivityStr) return;
    const loginTime = Number.parseInt(loginTimeStr, 10);
    const lastActivity = Number.parseInt(lastActivityStr, 10);
    if (now - loginTime >= this.SESSION_TIMEOUT) {
      this.triggerLogout();
      return;
    }
    if (now - lastActivity >= this.INACTIVITY_TIMEOUT) {
      this.triggerLogout();
    }
  }

  private triggerLogout(): void {
    this.stopMonitoring();
    this.ngZone.run(() => {
      if (this.logoutCallback) {
        this.logoutCallback();
      }
    });
  }

  private readonly eventHandler = (): void => {
    sessionStorage.setItem(this.LAST_ACTIVITY_KEY, Date.now().toString());
  };

  private startEventListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.eventHandler, { passive: true });
      document.addEventListener('keydown', this.eventHandler, { passive: true });
      document.addEventListener('click', this.eventHandler, { passive: true });
      document.addEventListener('touchstart', this.eventHandler, { passive: true });
      document.addEventListener('scroll', this.eventHandler, { passive: true });
    });
  }

  private removeEventListerns(): void {
    this.ngZone.runOutsideAngular(() => {
      document.removeEventListener('mousemove', this.eventHandler);
      document.removeEventListener('keydown', this.eventHandler);
      document.removeEventListener('click', this.eventHandler);
      document.removeEventListener('touchstart', this.eventHandler);
      document.removeEventListener('scroll', this.eventHandler);
    });
  }
}
